import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  canAccessRoute,
  canAccessBillingDashboard,
  canAccessDashboard,
  canApproveStudyDeletion,
  canAnonymizePatients,
  canExportCsv,
  canExportPatientCsv,
  canExportReportsCsv,
  canFilterAllUsers,
  canFilterHospital,
  canViewReportsList,
} from "@/lib/rbac";

/** Role-based UI access — single source of truth for routes and actions. */
export function useRbac() {
  const { user, permissions } = useAuth();
  const role = user?.role;
  const isIndividualDoctor = Boolean(user?.individual_doctor);

  return useMemo(() => {
    const rbacOptions = { individualDoctor: isIndividualDoctor };
    const route = (path) => canAccessRoute(permissions, path, role, rbacOptions);

    return {
      role,
      user,
      permissions,
      isIndividualDoctor,
      canAccessRoute: route,
      canViewDashboard: canAccessDashboard(permissions, role),
      canViewBilling: canAccessBillingDashboard(permissions, role),
      canManagePatients: canAnonymizePatients(role),
      canUpload: route("/upload"),
      canShare: route("/sharing"),
      canViewStudies: route("/viewer"),
      canViewReports: canViewReportsList(role, rbacOptions),
      canViewSearch: route("/search"),
      canViewAudit: route("/audit"),
      canManageUsers: route("/users"),
      canViewFlowchart: route("/flowchart"),
      canApproveStudyDeletion: canApproveStudyDeletion(role, rbacOptions),
      canAnonymizePatients: canAnonymizePatients(role),
      canExportPatientCsv: canExportPatientCsv(role),
      canExportCsv: canExportCsv(role),
      canExportReportsCsv: canExportReportsCsv(role, rbacOptions),
      canFilterHospital: canFilterHospital(role, rbacOptions),
      canFilterAllUsers: canFilterAllUsers(role),
    };
  }, [permissions, role, user, isIndividualDoctor]);
}
